import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMatch } from '@reach/router'
import { Helmet } from 'react-helmet'
import { useQuery } from 'react-query'
import { SortBy } from 'src/components/shared/EnhancedTable/AsyncEnhancedTable'
import { MESSAGES, ACUSES, TRASLADOS } from 'src/constants/routes'
import { useLexnetStatusContext } from 'src/contexts/LexnetStatusContext'
import { TipoMensaje } from 'src/enums/EscritosLegales'
import { EstadoMensaje, EstadoLexnetMensaje } from 'src/enums/EstadoMensaje'
import { getMessages } from 'src/services/MessageService'
import MessagesView from './MessagesView'

type State = {
  pageIndex: number
  pageSize: number
  sortBy: SortBy[]
  searchedText: string
}

export default function Messages(): JSX.Element {
  const { connected, lexnetUser } = useLexnetStatusContext()
  const isInFirmadas = useMatch(MESSAGES('firmadas')) !== null
  const isInPendientes = useMatch(MESSAGES('pendientes')) !== null

  let isAcuse = useMatch(ACUSES()) !== null
  const isAcusePendiente = useMatch(ACUSES('pendientes')) !== null
  const isAcuseAceptado = useMatch(ACUSES('aceptados')) !== null
  const isAcuseRechazado = useMatch(ACUSES('rechazados')) !== null

  const isTraslado = useMatch(TRASLADOS) !== null

  isAcuse = isAcuse || isAcusePendiente || isAcuseAceptado || isAcuseRechazado

  const [paginationOptions, setPaginationOptions] = useState<State>({
    pageIndex: 0,
    pageSize: 10,
    sortBy: [],
    searchedText: ''
  })
  const tipoMensaje = useMemo(() => {
    if (isAcuse) {
      return [TipoMensaje.ACUSE]
    }
    if (isTraslado) {
      return [TipoMensaje.ESCRITO, TipoMensaje.TRASLADO]
    }
    return [TipoMensaje.NOTIFICACION]
  }, [isAcuse, isTraslado])

  const estado = useMemo(() => {
    if (isInFirmadas) {
      return [EstadoMensaje.DESCARGADO, EstadoMensaje.PENDIENTE_DESCARGA, EstadoMensaje.ASIGNADO_EXPEDIENTE, EstadoMensaje.FIRMADO_EN_ORIGEN]
    }
    if (isInPendientes) {
      return [EstadoMensaje.INITIAL]
    }
    return []
  }, [isInFirmadas, isInPendientes])

  const estadoLexnet = useMemo(() => {
    if (isAcusePendiente) {
      return [EstadoLexnetMensaje.INITIAL]
    }
    if (isAcuseAceptado) {
      return [EstadoLexnetMensaje.ACEPTADO]
    }
    if (isAcuseRechazado) {
      return [EstadoLexnetMensaje.RECHAZADO, EstadoLexnetMensaje.SUBSANADO]
    }
    return []
  }, [isAcusePendiente, isAcuseAceptado, isAcuseRechazado])

  const queryKey = [
    'messagesListed',
    estado,
    estadoLexnet,
    tipoMensaje,
    paginationOptions.pageIndex,
    paginationOptions.pageSize,
    paginationOptions.searchedText
  ]
  if (paginationOptions.sortBy.length > 0) {
    queryKey.push(paginationOptions.sortBy[0].id)
    queryKey.push(paginationOptions.sortBy[0].desc ? 'desc' : 'asc')
  }

  const messagesQuery = useQuery(queryKey, () =>
    getMessages(
      { estado, estadoLexnet, tipoMensaje },
      paginationOptions.pageIndex + 1,
      paginationOptions.pageSize,
      paginationOptions.sortBy,
      paginationOptions.searchedText
    )
  )

  const { refetch } = messagesQuery
  useEffect(() => {
    if (connected) {
      refetch()
    }
  }, [connected, refetch])

  useEffect(() => {
    refetch()
  }, [lexnetUser, refetch])

  const onChangePaginationOptions = useCallback(function(value) {
    setPaginationOptions(value)
  }, [])

  // let section = 'Operativa LexNet'
  let subsection = 'Todas las notificaciones'

  if (isAcuse) {
    // section = 'Acuse recibo'
    subsection = 'Todos los acuses'
  }

  if (isInFirmadas) {
    subsection = 'Notificaciones firmadas'
  }

  if (isInPendientes) {
    subsection = 'Notificaciones pendientes de firma'
  }

  if (isAcuseAceptado) {
    subsection = 'Acuses aceptados'
  }

  if (isAcusePendiente) {
    subsection = 'Acuses pendientes'
  }

  if (isAcuseRechazado) {
    subsection = 'Acuses rechazados'
  }

  if (isTraslado) {
    // section = 'TRASLADO COPIA'
    subsection = 'TODOS LOS TRASLADOS'
  }

  const showInfoAlert = !isAcuse && !isTraslado

  return (
    <>
      <Helmet>
        <title>Mensajes</title>
      </Helmet>
      <MessagesView
        messagesQuery={messagesQuery}
        onChangePaginationOptions={onChangePaginationOptions}
        tipoMensaje={tipoMensaje}
        subsection={subsection}
        pageSize={paginationOptions.pageSize}
        showInfoAlert={showInfoAlert}
      />
    </>
  )
}
